import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Button, Chip, CircularProgress, Grid, Link, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useTranslation } from 'react-i18next';
import InlineBanner from '../components/InlineBanner';
import TokenMappingTable from '../components/TokenMappingTable';
import WhatsAppPhonePreview from './WhatsAppPhonePreview';
import WaRemoveConfirmDialog from './WaRemoveConfirmDialog';
import WaRemoveWaOnlyDialog from './WaRemoveWaOnlyDialog';
import { runWaRemoval, WaRemovalChoice } from './waRemoval';
import { whatsappRoutes } from '../../Whatsapp/Constant';
import { SmartSendTokenInfo } from '../../../Models/DataSources/SmartSend';
import { WaVariableMap } from '../../../Models/DataSources/SmartSendWhatsApp';
import {
    deleteWaMapping, setWaVariable, getWaMapping,
    getWaMemorySuggestions, waMemorySuggestionsKey, WaMemorySuggestionsArg,
} from '../../../redux/reducers/smartSendWhatsAppSlice';
import {
    clampStaticValue, duplicateHeaderColumnIds, isBound, sameAsEmailCandidates,
    composeWaSuggestion, memoryColumnName,
    STATIC_OPTION, waVariables,
} from './waTokens';
import {
    selectWaLegState, selectWaReceiptState, selectEmailOn, selectWaOnly,
    selectSourceHasEmailIdentity, WaRootState,
} from './selectors';
import { UseWaAutosave } from './useWaAutosave';
import useWaErrorText from './errorText';

// SmartSend part B — the WhatsApp tab of the mapping screen.
//
// The six leg states of contract §6 are branched HERE, and the order is not cosmetic: (5) is
// tested before (1) and (1b) before the plain (1), because both of those otherwise fall through
// to "אשר תבנית" — which is the button that creates a NEW header, overwrites the leg pointer and
// invites a second charge of the whole audience. `selectWaLegState` owns the order; this file
// only renders what it returns.
//
// 🔴 Nothing here decides whether a WRITE is allowed. That is `useWaAutosave.canWrite`, one
// choke point for the whole tab, so "the tab is locked" is a property of the effect and not of
// a list of disabled buttons (invariants 24 and 26). The buttons below reflect the same fact;
// they do not enforce it.

const useStyles = makeStyles((theme) => ({
    wrap: { marginTop: theme.spacing(1) },
    statusRow: {
        display: 'flex', alignItems: 'center', gap: theme.spacing(1.5), flexWrap: 'wrap',
        marginBottom: theme.spacing(2),
    },
    // One line, not a bordered card (owner, stage 2026-09-28: the card took a lot of room for a
    // name, a language and a count).
    templateLine: { marginBottom: theme.spacing(1.5), overflowWrap: 'anywhere' },
    templateName: { fontWeight: 600, color: (theme.palette.type === 'dark' ? (theme as any).custom.textPrimary : theme.palette.text.primary) },
    // The phone stays in view while a long variable table scrolls — below the sticky tab strip
    // (ChannelTabs, ~49px) so the two never overlap. Only in the two-column layout (md, 960) and only
    // on a screen tall enough to hold the whole column (~500px, more with a banner) between that strip
    // and the sticky ActionBar: on a short screen a pinned column would keep its bottom under the bar
    // until the table ends, so there it scrolls with the page as before.
    previewCol: {
        '@media (min-width: 960px) and (min-height: 760px)': { position: 'sticky', top: 56, alignSelf: 'flex-start' },
    },
    actions: { display: 'flex', alignItems: 'center', gap: theme.spacing(1.5), flexWrap: 'wrap', marginTop: theme.spacing(2) },
    saveState: { display: 'flex', alignItems: 'center', gap: theme.spacing(1) },
    error: { color: 'var(--pls-d-danger, #8a1c1c)', fontWeight: 600 },
}));

interface Props {
    campaignId: number;
    /** The app-wide sheet, forwarded to the phone preview. */
    classes: any;
    autosave: UseWaAutosave;
    /** The picker got back the EXISTING unit for this choice (`?reused=1`) — its map is already filled. */
    reused?: boolean;
    /**
     * Whether the phone preview is rendered INSIDE this tab (default). The WhatsApp unit's own page
     * renders it beside the page header instead (SmartSendScreen `pagePreview`), so it starts at the
     * top of the page; there this tab is the mapping column alone.
     */
    showPreview?: boolean;
    /**
     * 12-fix-wave 7.6 — the screen's «הסר» of the WhatsApp on a "both" page (7.4(b): POST Leave, then
     * the e-mail page). The remove action below routes to it while the send holds a present unit.
     */
    onLeave?: (() => void) | null;
    /** QA BUG-06 — the D1 text while the send is closed (SmartSendScreen `sendIsClosed`), else null: the
     *  hover reason of the variable table when it is read-only. */
    closedReason?: string | null;
}

const WhatsAppMappingTab: React.FC<Props> = ({
    campaignId, classes: appClasses, autosave, reused = false, showPreview = true, onLeave = null, closedReason = null,
}) => {
    // 09-channel-units — `campaignId` IS the WhatsApp unit's own id (a WACampaignID) on every page
    // that shows this tab, "both" included, so every request from here is on channel 3. The screen
    // passes the unit; this tab never sees an e-mail campaign id.
    const mappingChannel: number = 3;
    const classes = useStyles();
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { waErrorText } = useWaErrorText();

    // v1.23b — which of the two `noCell` wordings is true right now. Inside this tab the
    // add-on is active by construction, so "both channels lit" reduces to "the e-mail is on".
    const emailOn = useSelector((s: WaRootState) => selectEmailOn(s));
    // v1.23c — which removal dialog this campaign gets, and whether "switch to e-mail" is a real
    // option on it. This screen is the one place BOTH facts are known: the leg is loaded and
    // `SourcePicker` has fetched the source list right above.
    const waOnly = useSelector((s: WaRootState) => selectWaOnly(s));
    const canSwitchToEmail = useSelector((s: WaRootState) => selectSourceHasEmailIdentity(s));
    const [mappingDeleteFailed, setMappingDeleteFailed] = useState(false);
    const legState = useSelector((s: WaRootState) => selectWaLegState(s));
    const receiptState = useSelector((s: WaRootState) => selectWaReceiptState(s));
    const mapping = useSelector((s: any) => s.smartSendWa.waMapping);
    // 12-fix-wave 7.6 — which removal applies. 'both' ⇔ the e-mail page's row holds a present unit
    // (RowWACampaignID != null): remove = POST Leave (`onLeave`). The leg path (`runWaRemoval`, the
    // leg delete) only on an e-mail page with no unit that still has a leg. A unit's own page: neither.
    const unitModeTab: string | null = useSelector((s: any) => s.smartSendWa.unitMode ?? null);
    const leavePath = unitModeTab === 'both' && !!onLeave;
    const legPath = unitModeTab === 'email' && !!mapping?.HasLeg;
    // §1.9 — enabled on both paths only while both channels are editable.
    const bothChannelsEditable = leavePath
        ? (mapping?.WaCampaignStatus === 1 && (mapping?.EmailCampaignStatus ?? 1) === 1)
        : ((mapping?.WaCampaignStatus ?? 1) === 1 && (mapping?.EmailCampaignStatus ?? 1) === 1);
    // The add-on answered Enabled but not Ready (a sender number not connected, no approved template…).
    // The channel tiles used to say why; this page no longer has them, so the tab says it — in the
    // tile's words, with the same templates link (this tab renders only with AllowWhatsAppToAgent,
    // which is that link's own gate). Scalars: a fresh object would re-render on every dispatch.
    const waReady = useSelector((s: any) => !!s.smartSendWa.waAvailability?.Ready);
    const waBlockReason: string | null = useSelector((s: any) => s.smartSendWa.waAvailability?.BlockReason ?? null);
    // 🔴 C-40 — a refused `GET WhatsApp/Mapping`. Reachable on this screen precisely because
    // C-35 lights the channel from the URL: the tab now renders even when the read failed,
    // which is the whole point — before C-35 the operator got no tab and no explanation, and
    // after it they would have got a tab and no explanation.
    const mappingStatus = useSelector((s: any) => s.smartSendWa.mappingStatus);
    const mappingError = useSelector((s: any) => s.smartSendWa.mappingError);
    const [reloading, setReloading] = useState(false);
    const placeholderCount: number = useSelector((s: any) => s.smartSendWa.waPlaceholderCount ?? 0);
    const variableMap: WaVariableMap = useSelector((s: any) => s.smartSendWa.waVariableMap);
    const templateCard = useSelector((s: any) => s.smartSendWa.waTemplateCard);
    const saveState = useSelector((s: any) => s.smartSendWa.waSaveState);
    const lastSaveError = useSelector((s: any) => s.smartSendWa.lastSaveError);
    // The e-mail side, read-only — the "כמו במייל" chips are built from what the e-mail tab
    // already maps. `smartSendSlice` is never written to from here.
    const emailTokens = useSelector((s: any) => s.smartSend.tokens);
    const emailTokenMap = useSelector((s: any) => s.smartSend.tokenMap);
    const columns = useSelector((s: any) => s.smartSend.columns);

    const [removeOpen, setRemoveOpen] = useState(false);
    // Same refusal surface as the picker's copy of this dialog (C-2). `_SetMapping @prm_Delete=1`
    // answers `-2` ⇒ 409 `WA_ALREADY_SENT` in the residual race, and `.rejected` on a dropped
    // request — and this dialog used to CLOSE on both, which reads as "removed" when nothing was.
    const [removeError, setRemoveError] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);

    const { canWrite, markWaEdited, saveWaMapping, cancelPending } = autosave;

    // ── the table's tokens ──────────────────────────────────────────────────────────────────
    // A WhatsApp placeholder has no name — it is a POSITION. `Token` carries the index as a
    // string so the shared table can key rows by it, and `renderTokenLabel` prints `{{n}}`.
    const tokens: SmartSendTokenInfo[] = useMemo(
        () => waVariables(placeholderCount).map((v) => ({
            Token: String(v.index),
            IsSystemField: false,
            IsGraphToken: false,
            MappedColumnID: variableMap[v.index]?.columnId ?? null,
        })),
        [placeholderCount, variableMap],
    );

    const value = useMemo(() => {
        const m: { [token: string]: number | null } = {};
        waVariables(placeholderCount).forEach((v) => { m[String(v.index)] = variableMap[v.index]?.columnId ?? null; });
        return m;
    }, [placeholderCount, variableMap]);

    // ≤3 columns the E-MAIL maps, offered one per unmapped row and never twice. Deliberately NOT
    // name similarity: a `{{3}}` has no name, so there is nothing to score — and invariant 9
    // forbids filling a column without a click in any case.
    // ⚠️ THE CAP MOVED MEANING, AND THAT IS WHY IT IS STATED HERE. `sameAsEmailCandidates`
    // defaults to three, which was "at most three chips on screen" while every row showed the
    // SAME chip. Per variable it becomes "at most three variables can be offered anything",
    // which is a different and much tighter limit. The e-mail side's mapped columns are the
    // whole candidate pool; there is no reason to withhold the fourth from `{{4}}`.
    const candidates = useMemo(
        () => sameAsEmailCandidates(emailTokens ?? [], emailTokenMap ?? {}, columns ?? [], 20),
        [emailTokens, emailTokenMap, columns],
    );

    // 🔴 C8 / contract §6 — "בורר העמודות מציג chip «כותרת כפולה — נבחרה הראשונה» כשיש
    // `SourceHeader` כפול". The mapping is stored and resolved BY HEADER, not by ColumnID
    // (`_SetMapping_Stage` writes `SourceHeader`; reads resolve `MIN(ColumnID)` per
    // `(DataSourceVersionID, SourceHeader)`), so picking the second of two identically-headed
    // columns silently sends the FIRST one's values. Until now the key existed in all three
    // languages and nothing rendered it — half the clause built, half not.
    const dupColumnIds = useMemo(() => duplicateHeaderColumnIds(columns ?? []), [columns]);

    // ══ 08-mapping-memory — "USED LAST TIME" ═══════════════════════════════════════════════
    // What this sub-account chose last time for THIS template's variables, resolved against the
    // version on screen. ONE GET on entering the tab with a template and a version (plan v2 §4.5),
    // and another only when a part of the key moves — campaign, container channel, version,
    // template — never per edit, never per autosave. A failed or absent answer leaves the tab
    // exactly as it was before this feature: the positional offer below, and nothing else.
    //
    // 🔴 THE TEMPLATE IS THE ONE ON SCREEN, AND IT IS SENT (Idan E1, 2026-09-23). The route takes
    // both memory keys from the request, so the tab asks the moment it opens — including the §1
    // case, the FIRST mapping of a unit, before any variable was saved. (09-channel-units: a unit
    // is created with its own row and template by POST WhatsApp/Container, so its GET answers from
    // the start.) Nothing waits for a first save any more. `whatsAppTemplateId` is the
    // template this tab renders and the one `useWaAutosave` saves as `WhatsAppTemplateID`, and it is
    // part of the key, so rows for template A can never be offered on template B's variables —
    // index 2 of A is not index 2 of B.
    //
    // ⚠️ THE VERSION IS `smartSend.lockedVersionId` (Idan E2) — the version of the `columns` this
    // table lists and the one the e-mail save posts as `DataSourceVersionID`. Once the campaign is
    // bound it is the server's locked version (`getMapping.fulfilled`); before that it is the picked
    // source's ActiveVersionID (`loadSourceColumns.fulfilled`), so a brand-new campaign has it as
    // soon as its source's columns are on screen. It is trusted only once THIS campaign's e-mail
    // mapping has loaded — `smartSend` outlives a campaign-to-campaign navigation.
    const emailSliceCampaignId = useSelector((s: any) => s.smartSend.campaignId);
    // 09-channel-units — "THIS campaign" is the PAGE, not this tab's unit: on a "both" page the tab's
    // `campaignId` is the WhatsApp unit's own id while the e-mail slice holds the e-mail campaign's,
    // so comparing the two never matched there and the memory was never asked for. Both slices are
    // keyed on the page's id by `setCampaignContext`.
    const pageCampaignId = useSelector((s: any) => s.smartSendWa.campaignId);
    const emailMappingStatus = useSelector((s: any) => s.smartSend.mappingStatus);
    const versionId: number | null = useSelector((s: any) => s.smartSend.lockedVersionId ?? null);
    const templateOnScreen: string | null = useSelector((s: any) => s.smartSendWa.whatsAppTemplateId ?? null);
    const memoryAll = useSelector((s: any) => s.smartSendWa.waMemorySuggestions);
    const memoryLoadedKey = useSelector((s: any) => s.smartSendWa.waMemorySuggestionsKey);
    // No template on screen ⇒ nothing to ask about. Sent AS-IS, never trimmed: the save writes the
    // memory under exactly this string.
    const memoryTemplate: string | null = typeof templateOnScreen === 'string' && templateOnScreen.trim() !== ''
        ? templateOnScreen
        : null;
    const memoryArg: WaMemorySuggestionsArg | null = (
        campaignId > 0
        && pageCampaignId != null && emailSliceCampaignId === pageCampaignId && emailMappingStatus === 'succeeded'
        && versionId != null && versionId > 0
        && memoryTemplate != null
    ) ? { campaignId, channel: mappingChannel, dataSourceVersionId: versionId, templateId: memoryTemplate } : null;
    const memoryKey = memoryArg ? waMemorySuggestionsKey(memoryArg) : null;
    // Latched per KEY, the `urlSeeded` / `bindingWritten` pattern of SmartSendScreen: a key is
    // asked for once per mount, whatever it answered — so a route that fails (it answers 500 until
    // the SQL is deployed) is not retried in a loop, and an autosave, which moves none of the four
    // parts, can never re-fire it. Stale answers are dropped in the slice (`meta.requestId`).
    const memoryRequested = useRef<string | null>(null);
    useEffect(() => {
        if (!memoryArg || !memoryKey || memoryRequested.current === memoryKey) return;
        memoryRequested.current = memoryKey;
        dispatch(getWaMemorySuggestions(memoryArg) as any);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, memoryKey]);
    // Only rows that answer the key this tab would ask with NOW — see `waMemorySuggestionsKey`.
    const memoryRows = memoryKey != null && memoryLoadedKey === memoryKey && Array.isArray(memoryAll)
        ? memoryAll
        : null;
    const liveColumnIds = useMemo(() => new Set<number>((columns ?? []).map((c: any) => c.ColumnID)), [columns]);

    // 🔴 PER VARIABLE, not "the first free column over and over" — see `suggestionForVariable`.
    // `tok.Token` carries the VariableIndex on this side (a WhatsApp variable is a position,
    // `{{1}}`, not a name), and it was being ignored: every empty row showed the same chip.
    // 08-mapping-memory — the chain is `composeWaSuggestion`: memory → this positional offer →
    // nothing, MEMORY WINS. Name similarity is never a link on this tab, which is why this table
    // is handed a `suggestionSource` and never `fallbackToSimilarity`. `pickFor` is the one place
    // the chain is evaluated, so the bubble and the duplicate-header badge below cannot disagree
    // about which offer — memory or positional — a row is showing.
    const pickFor = (tok: SmartSendTokenInfo) => {
        const used = new Set(
            Object.keys(variableMap)
                .map((k) => variableMap[Number(k)]?.columnId)
                .filter((c): c is number => c != null && c > 0),
        );
        return composeWaSuggestion(Number(tok.Token), memoryRows, liveColumnIds, candidates, used);
    };
    const suggestionSource = (tok: SmartSendTokenInfo) => {
        const pick = pickFor(tok);
        if (pick == null) return null;
        if (pick.source === 'memory') {
            return {
                columnId: pick.columnId,
                label: t('DataSources.send.mapping.usedLastTime', { name: memoryColumnName(pick.row, columns) }),
            };
        }
        const columnId = pick.columnId;
        const col = (columns ?? []).find((c: any) => c.ColumnID === columnId);
        return {
            columnId,
            label: t('DataSources.send.whatsapp.mapping.sameAsEmail', {
                name: col?.DisplayName || col?.SourceHeader || '',
            }),
        };
    };

    // 2026-09-28 — what the table header may apply in ONE click: the memory offers only. The positional
    // "כמו במייל" offer is a guess about which column a `{{n}}` means, so it stays one row at a time.
    const memoryOnlySource = (tok: SmartSendTokenInfo) => {
        const pick = pickFor(tok);
        return pick != null && pick.source === 'memory' ? suggestionSource(tok) : null;
    };

    const onColumnChange = (tokenKey: string, columnId: number | null) => {
        if (!canWrite) return;
        dispatch(setWaVariable({ variableIndex: Number(tokenKey), columnId, staticValue: null }));
        markWaEdited();
    };

    const onStaticChange = (tok: SmartSendTokenInfo, v: string | null) => {
        if (!canWrite) return;
        dispatch(setWaVariable({
            variableIndex: Number(tok.Token),
            columnId: null,
            staticValue: v == null ? null : clampStaticValue(v),
        }));
        markWaEdited();
    };

    // ── which of the three buttons exist in this state ──────────────────────────────────────
    // "הסר וואטסאפ" is rendered in EXACTLY three states — no leg, state (1), and
    // `WaCampaignStatus === 1` (states 3/4) — and in all three it is ALSO subject to
    // `LastErrorCharged === 0` (contract §5, word for word). `@prm_Delete=1` deletes the LEG
    // ROW itself, which is the only pointer through which the charge flag is computed, so while
    // that flag is lit the button is hidden in every state — remove-then-repick would otherwise
    // return the screen to a clean state (1) and charge the audience twice in two clicks.
    const charged = !!mapping?.LastErrorCharged;
    // 🔴 NOT ON A STAND-ALONE CONTAINER. This button calls DELETE WhatsApp/Mapping/{id}, which is
    // the LEG route — it proves ownership against dbo.Campaigns and deletes a leg row that a
    // channel-3 send does not have.
    // 09-channel-units — and since every WhatsApp tab is now a unit on channel 3, this button no
    // longer renders anywhere. Removing the WhatsApp from a "both" send is "הסר" on its tab
    // (ChannelTabs; it deletes the unit through `DELETE DeleteMapping/{unit}?channel=3`); a
    // WhatsApp-only unit is deleted from the manage tab. The leg branch stays only because a leg is
    // still a legal server object for campaigns saved before the units.
    const showRemove = !charged && mappingChannel !== 3 && (leavePath || legPath) && bothChannelsEditable && (
        legState === 'noLeg' || legState === 'noHeader'
        || ((legState === 'waiting' || legState === 'normal') && mapping?.WaCampaignStatus === 1)
    );
    const showConfirmTemplate = (legState === 'noLeg' || legState === 'noHeader');
    const readOnlyTable = !canWrite;

    const doRemove = async () => {
        // 12-fix-wave 7.6 — a send holding a present unit leaves through POST Leave (7.4(b)).
        if (leavePath && onLeave) { setRemoveOpen(false); cancelPending(); onLeave(); return; }
        if (!legPath) return;
        setBusy(true);
        setRemoveError(null);
        cancelPending();
        const res: any = await dispatch(deleteWaMapping(campaignId) as any);
        setBusy(false);
        const p = res && res.payload ? res.payload : {};
        if (p.StatusCode !== 200 && p.StatusCode !== 201) {
            // Stays open and says why. Closing here announced a removal that did not happen —
            // and the leg is the evidence key for the report, the supervisor mail and "view
            // message", so "it is gone" is exactly the wrong thing to imply.
            setRemoveError(p.Message ?? 'WA_INTERNAL');
            return;
        }
        setRemoveOpen(false);
    };

    // 🔴 v1.23c/d — THE WHATSAPP-ONLY BRANCH. Removing the WhatsApp here is "turning off the
    // last channel" through a different door, and §6 forbids that state; so the operator
    // chooses, and both branches carry `confirmSuppressedDelete` (see `waRemoval.ts`).
    const doRemoveWaOnly = async (choice: WaRemovalChoice) => {
        // 12-fix-wave 7.6 — Leave when the send holds a present unit; `runWaRemoval` only for a leg.
        if (leavePath && onLeave) { setRemoveOpen(false); cancelPending(); onLeave(); return; }
        if (!legPath) return;
        setBusy(true);
        setRemoveError(null);
        setMappingDeleteFailed(false);
        cancelPending();
        const out = await runWaRemoval(dispatch, campaignId, choice);
        setBusy(false);
        if (out.ok) { setRemoveOpen(false); return; }
        // The two failures are NOT the same event and must not share a surface: a refused leg
        // delete changed nothing, while a refused mapping delete means the WhatsApp IS gone and
        // only the mapping survived. The dialog withdraws both actions on the second one.
        if (out.stage === 'mapping') { setMappingDeleteFailed(true); return; }
        setRemoveError(out.message ?? 'WA_INTERNAL');
    };

    const doConfirmTemplate = async () => {
        setBusy(true);
        cancelPending();
        await saveWaMapping();
        setBusy(false);
    };

    // 🔵 NO NEW i18n KEY WAS MINTED FOR THIS, AND THAT IS DELIBERATE (§9). The server already
    // names the reason in §4's own vocabulary — `WA_EMAIL_LEG_MISSING` is "אין מיפוי אימייל
    // לקמפיין", which is exactly true for the campaign that has not been saved yet — and
    // `waErrorText` is the one gate that turns a code into a sentence, with `i18n.exists` in
    // front of it. The retry reuses `send.errors.retryAction`, already on this screen.
    // ⚠️ If the orchestrator wants a bespoke sentence for this state, it is one key and one
    // line; it is NOT invented here.
    const reloadMapping = async () => {
        setReloading(true);
        await dispatch(getWaMapping({ campaignId, channel: mappingChannel }) as any);
        setReloading(false);
    };

    return (
        <Box className={classes.wrap}>
            {/* 🔴 C-40 (round 2) — `WA_EMAIL_LEG_MISSING` IS SILENT, AND THAT FOLLOWS FROM C-35.
                Once the URL seed stopped waiting for `GET Mapping`, this tab renders for a
                campaign that has never been saved — and for exactly that campaign the read
                answers `409 WA_EMAIL_LEG_MISSING` (`_GetMapping_Stage` ⇒ `-16`, no
                `CampaignsToDataSources` row yet). That is not a failure, it is the NORMAL
                opening state, and a banner there would greet every operator on every new
                campaign. The screen already tells them what to do next: pick a source, map the
                variables, confirm the template.
                ⚠️ Every OTHER refusal still speaks — a silent screen was the whole of C-40. */}
            {mappingStatus === 'failed' && mappingError !== 'WA_EMAIL_LEG_MISSING' && (
                <Box className={classes.statusRow}>
                    <InlineBanner
                        severity="warning"
                        role="alert"
                        title={waErrorText(mappingError)}
                        action={(
                            <Button size="small" variant="outlined" color="primary" disabled={reloading}
                                onClick={reloadMapping}>
                                {reloading
                                    ? <CircularProgress size={14} />
                                    : t('DataSources.send.errors.retryAction')}
                            </Button>
                        )}
                    />
                </Box>
            )}
            {!waReady && waBlockReason && (
                <Box className={classes.statusRow}>
                    <InlineBanner
                        severity="warning"
                        role="status"
                        title={t(`DataSources.send.whatsapp.blocked.${waBlockReason}`)}
                        action={(
                            <Link href={whatsappRoutes.TEMPLATE_MANAGEMENT} underline="always">
                                {t('DataSources.send.whatsapp.blocked.manageTemplates')}
                            </Link>
                        )}
                    />
                </Box>
            )}
            {/* ── state lines ─────────────────────────────────────────────────────────────── */}

            {/* (5) the header was deleted AFTER sending. The wording is deliberately careful:
                `SentHeaderDeleted` lights on ANY status ≠ 1, including 2 (in flight) and 3
                (stopped from the WhatsApp UI), so only part of the audience may have received
                anything. A blanket "the messages went out" would be exactly the false statement
                that produced Z-01. None of the header counters exist here, so `receipt.stuck` /
                `receipt.inFlight` are not computed and not shown — the report is the only
                source of truth for what actually went out. */}
            {legState === 'headerDeleted' && (
                <Box className={classes.statusRow}>
                    <InlineBanner
                        severity="warning"
                        role="status"
                        title={t('DataSources.send.whatsapp.mapping.waHeaderDeletedAfterSend')}
                        action={(
                            <Link href={whatsappRoutes.REPORTS} underline="always">
                                {t('DataSources.send.whatsapp.receipt.links.waReport')}
                            </Link>
                        )}
                    />
                </Box>
            )}

            {/* (1b) charged, no header. The screen is locked until the owner refunds the SAME
                @Guid — and it then unlocks BY ITSELF (the flag is "charged and not refunded"),
                so this is not a dead end and needs no new action on screen. */}
            {legState === 'chargedNoHeader' && (
                <Box className={classes.statusRow}>
                    <InlineBanner
                        severity="warning"
                        role="status"
                        title={t('DataSources.send.whatsapp.receipt.chargedNoSend')}
                    />
                </Box>
            )}

            {/* (2) the WhatsApp campaign has sent — the map is frozen; `_SetMapping` answers -2. */}
            {legState === 'locked' && (
                <Typography variant="body2" color="textSecondary" className={classes.statusRow}>
                    {t('DataSources.send.whatsapp.mapping.lockedAfterSend')}
                </Typography>
            )}

            {/* (3) the e-mail is out and the WhatsApp is not — the late-send path. The row is
                driven by SERVER state alone, so it survives a refresh and a late entry; the
                v1.7 button that lived on the `lockedAfterSend` row could never render, because
                that row requires `WaCampaignStatus ≠ 1` and the retry requires `= 1`. */}
            {legState === 'waiting' && (
                <Box className={classes.statusRow}>
                    <Typography variant="body2" color="textSecondary">
                        {t('DataSources.send.whatsapp.mapping.waPending')}
                    </Typography>
                    {charged && (
                        <Chip size="small" variant="outlined"
                            label={t('DataSources.send.whatsapp.receipt.chargedNoSend')} />
                    )}
                    {/* 12-fix-wave 7.5 (D2) — no retry of a sent send: the retry button was removed. */}
                </Box>
            )}

            {/* The three send-state receipts, first match wins (contract §7): stuck → queued →
                inFlight. `receipt.stuck` never offers a retry. */}
            {receiptState === 'stuck' && (
                <Typography variant="body2" className={classes.statusRow} style={{ color: 'var(--pls-d-danger, #8a1c1c)' }}>
                    {t('DataSources.send.whatsapp.receipt.stuck')}
                </Typography>
            )}
            {receiptState === 'inFlight' && (
                <Typography variant="body2" color="textSecondary" className={classes.statusRow}>
                    {t('DataSources.send.whatsapp.receipt.inFlight')}
                </Typography>
            )}
            {receiptState === 'sent' && (
                <Typography variant="body2" color="textSecondary" className={classes.statusRow}>
                    {t('DataSources.send.whatsapp.receipt.sent')}
                </Typography>
            )}

            {/* Not a blocker: the e-mail still goes out in full and only the WhatsApp is skipped
                (contract §6 — `blocked` never disables Send). */}
            {mapping?.HasLeg && mapping?.HasCellIdentity === false && (
                <Box className={classes.statusRow}>
                    <InlineBanner
                        severity="warning"
                        role="status"
                        // v1.23b — the parenthesis ("המייל יישלח כרגיל") is a promise about
                        // the OTHER channel, so it may only appear while that channel is lit.
                        // This banner is inside the WhatsApp tab, which only exists when the
                        // add-on is active, so "both" here reduces to "the e-mail is on".
                        title={t(emailOn
                            ? 'DataSources.send.whatsapp.mapping.noCellBannerBoth'
                            : 'DataSources.send.whatsapp.mapping.noCellBanner')}
                    />
                </Box>
            )}

            <Grid container spacing={2}>
                {/* `md` (960) is the two-column threshold — never `lg`. At 1366×768 with the
                    sidebar open the content column is ~1090px, so an `lg` breakpoint would stack
                    the phone under the table on the very laptop this screen is sized for.
                    The template line lives INSIDE the mapping column, so the phone column starts at
                    the top of the tab rather than below it (owner, stage 2026-09-28). */}
                <Grid item xs={12} md={showPreview ? 7 : 12}>
                    {/* The chosen template, on one line: its name, its language and how many
                        variables it has. */}
                    {templateCard && (
                        <Typography variant="body2" color="textSecondary" className={classes.templateLine}>
                            {`${t('DataSources.send.whatsapp.mapping.templateLabel')}: `}
                            <span className={classes.templateName}>{templateCard.name}</span>
                            {' · '}
                            {t('DataSources.send.whatsapp.picker.card.language', { lang: templateCard.language ?? '' })}
                            {' · '}
                            {t('DataSources.send.whatsapp.picker.card.placeholders', { n: (templateCard.variables ?? 0).toLocaleString() })}
                        </Typography>
                    )}
                    {/* The same choice is the same unit: the picker handed back the existing draft
                        for this template and source, so what the table shows was saved earlier,
                        not typed now. */}
                    {reused && (
                        <Typography variant="body2" color="textSecondary" className={classes.templateLine}>
                            {t('DataSources.send.whatsapp.mapping.reusedNotice')}
                        </Typography>
                    )}
                    <TokenMappingTable
                        tokens={tokens}
                        columns={columns ?? []}
                        value={value}
                        onChange={onColumnChange}
                        // The badge slot carries ONE thing here, and only when it is true: this
                        // row's chosen column shares its header with another column, so the
                        // server will resolve it to the first of them. The e-mail badges (graph /
                        // system-field / free) are token kinds and mean nothing for a positional
                        // WhatsApp variable, so the slot is otherwise empty.
                        //
                        // It rides on `renderBadge` — the one per-row slot that takes
                        // arbitrary content — so it lands in the same row as the Select it
                        // describes.
                        //
                        // ⚠️ CORRECTION (v1.21). An earlier version of this comment said the chip
                        // avoided "a tenth prop because §6 fixes the table at NINE". That was
                        // wrong twice over: this component ALREADY passes a tenth prop,
                        // `staticValueOf` (below), and §6 now enumerates TEN and names it —
                        // "**10 props אופציונליים** (🔴 v1.21: היו 9. נוסף `staticValueOf?`…)".
                        // `renderBadge` is still the right slot for the chip; the prop count was
                        // never the reason.
                        //
                        // 08-mapping-memory (plan v2 §3.7) — THE SAME CHIP, ALSO ON THE NEW MEMORY
                        // BUBBLE. Memory is resolved BY HEADER (lowest ColumnID wins), so a memory
                        // bubble on a duplicated header is exactly the case this chip exists for, and
                        // it has to be on screen BEFORE the click that accepts it, not only after.
                        // Same helper, same key, same slot — no second mechanism. `offered` mirrors
                        // the table's own chip condition (row not bound, table writable, and a source
                        // answer — whose column is always on screen), so the badge never names a
                        // column the row is not actually showing a bubble for.
                        // ⚠️ ONLY the memory bubble. The positional "כמו במייל" bubble keeps exactly
                        // what it had before this feature: the chip once its column is PICKED.
                        renderBadge={(tok) => {
                            const picked = variableMap[Number(tok.Token)]?.columnId ?? null;
                            const offer = picked == null && !readOnlyTable && !isBound(variableMap, Number(tok.Token))
                                ? pickFor(tok)
                                : null;
                            const offered = offer != null && offer.source === 'memory' ? offer.columnId : null;
                            const shown = picked ?? offered;
                            if (shown == null || !dupColumnIds.has(shown)) return null;
                            return (
                                <Chip
                                    size="small"
                                    variant="outlined"
                                    label={t('DataSources.send.whatsapp.mapping.duplicateHeader')}
                                />
                            );
                        }}
                        headerLabels={{
                            token: t('DataSources.send.whatsapp.mapping.variable'),
                            column: t('DataSources.send.whatsapp.mapping.column'),
                        }}
                        renderTokenLabel={(tok) => `{{${tok.Token}}}`}
                        extraOptions={[{ value: STATIC_OPTION, label: t('DataSources.send.whatsapp.mapping.staticValue') }]}
                        onStaticChange={onStaticChange}
                        staticValueOf={(tok) => variableMap[Number(tok.Token)]?.staticValue ?? null}
                        isMappedOverride={(tok) => isBound(variableMap, Number(tok.Token))}
                        suggestionSource={suggestionSource}
                        bulkSuggestionSource={memoryOnlySource}
                        emptyLabel={t('DataSources.send.mapping.noTokens')}
                        readOnly={readOnlyTable}
                        // QA BUG-06 — only the D1 reason; a table locked for another reason (the money
                        // lock 1b) keeps its banner and gets no tooltip.
                        readOnlyReason={closedReason}
                    />

                    {/* A mapped header that is no longer in the locked version. The server marks
                        it `ColumnInVersion=false`, the slice drops the id, and the row counts as
                        unmapped — this line names WHICH header vanished so the operator can pick
                        a replacement instead of guessing. */}
                    {(mapping?.Map ?? [])
                        .filter((r: any) => r.ColumnInVersion === false && r.SourceHeader)
                        .map((r: any) => (
                            <Typography key={r.VariableIndex} variant="body2" style={{ color: '#8a5a00', marginTop: 8 }}>
                                {t('DataSources.send.whatsapp.mapping.columnMissing', { header: r.SourceHeader })}
                            </Typography>
                        ))}
                </Grid>

                {showPreview && (
                    <Grid item xs={12} md={5} className={classes.previewCol}>
                        <WhatsAppPhonePreview campaignId={campaignId} classes={appClasses} />
                    </Grid>
                )}
            </Grid>

            <Box className={classes.actions}>
                {showConfirmTemplate && (
                    <Button variant="contained" color="primary" disabled={busy || !canWrite} onClick={doConfirmTemplate}>
                        {busy ? <CircularProgress size={18} color="inherit" /> : t('DataSources.send.whatsapp.mapping.confirmTemplate')}
                    </Button>
                )}
                {showRemove && (
                    <Button variant="text" disabled={busy} style={{ color: 'var(--pls-d-text-2, #8a8a8a)' }}
                        onClick={() => { setRemoveError(null); setRemoveOpen(true); }}>
                        {t('DataSources.send.whatsapp.mapping.removeWhatsApp')}
                    </Button>
                )}
                <Box style={{ flex: 1 }} />
                <Box className={classes.saveState} aria-live="polite">
                    {saveState === 'saving' && <CircularProgress size={16} />}
                    {saveState === 'error' && (
                        <Typography variant="body2" className={classes.error}>
                            {/* The §4 `Message` code, never the raw server string — and never
                                the raw KEY either: `waErrorText` gates on `i18n.exists`. */}
                            {/* 12-fix-wave 7.10 — on this page a 409 SEND_ALREADY_SENT reads "send closed". */}
                            {lastSaveError === 'SEND_ALREADY_SENT'
                                ? t('DataSources.send.errors.sendClosed')
                                : waErrorText(lastSaveError)}
                        </Typography>
                    )}
                </Box>
            </Box>

            {/* TWO dialogs, one flag. In the "both channels" state this is the confirmation the
                picker also raises — same destructive action, same four strings, untouched
                (owner decision 2026-09-13). In WhatsApp-only it is the CHOICE dialog, because
                "המייל יישלח כרגיל" would be switching the mail back on in silence. */}
            {waOnly ? (
                <WaRemoveWaOnlyDialog
                    open={removeOpen}
                    busy={busy}
                    canSwitchToEmail={canSwitchToEmail}
                    error={removeError}
                    mappingDeleteFailed={mappingDeleteFailed}
                    onChoose={doRemoveWaOnly}
                    onCancel={() => { setRemoveError(null); setMappingDeleteFailed(false); setRemoveOpen(false); }}
                />
            ) : (
                <WaRemoveConfirmDialog
                    open={removeOpen}
                    busy={busy}
                    error={removeError}
                    onConfirm={doRemove}
                    onCancel={() => { setRemoveError(null); setRemoveOpen(false); }}
                />
            )}
        </Box>
    );
};

export default WhatsAppMappingTab;
